import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { AsyncSelect } from 'src/components/forms';
import { ITableColumn, TableList } from 'src/components/table';
import { EntityWithIdAndDescriptionFields } from 'src/types';
import { OperacionAFOPUFields, OperacionAFOPUTablaValorFields } from '../types/fields';
import { AFOPURow, AFOPUHeader } from '../hooks/useTablaValorAFOPU';

interface AFOPUTablaValoresEditorGridProps {
  formMethods: UseFormReturn<any>;
  headers: AFOPUHeader[];
  rows: AFOPURow[];
  aforos: any[];
  loadAforoTipos: any;
  isLoading: boolean;
  permiteEditar?: boolean;
}

const AFOPUTablaValoresEditorGrid = ({
  formMethods,
  headers,
  rows,
  aforos,
  loadAforoTipos,
  isLoading,
  permiteEditar = true
}: AFOPUTablaValoresEditorGridProps): JSX.Element => {

  const { control } = formMethods;

  // para mapear celdas a indices del formulario
  const indexMap = useMemo(() => {
    const map = new Map<string, number>();
    aforos.forEach((v, idx) => {
      const codInst = v[OperacionAFOPUTablaValorFields.CodInstrumento];
      const codTipo = v[OperacionAFOPUTablaValorFields.CodOperacionTipo];
      map.set(`${codInst}-${codTipo}`, idx);
    });

    return map;
  }, [aforos]);

  const columns: ITableColumn[] = useMemo(() => {
    const cols: ITableColumn[] = [
      {
        label: 'Instrumento',
        onRenderCell: (row: AFOPURow) => <b>{row.descInstrumento}</b>
      }
    ];

    headers.forEach((header) => {
      cols.push({
        label: header.descOperacionTipo,
        onRenderCell: (row: AFOPURow) => {
          const celda = row.celdas[header.codOperacionTipo];
          if (!celda) return <span>-</span>;

          const key = `${celda[OperacionAFOPUTablaValorFields.CodInstrumento]}-${celda[OperacionAFOPUTablaValorFields.CodOperacionTipo]}`;
          const index = indexMap.get(key);

          if (index === undefined) return <Skeleton variant='text' />;

          const fieldPath = `${OperacionAFOPUFields.TablaValores}.${index}.${OperacionAFOPUTablaValorFields.Valor}`;

          return (
            <AsyncSelect
              loadOptions={loadAforoTipos}
              label=''
              control={control}
              name={fieldPath}
              disabled={!permiteEditar || !celda.permiteEditar}
              valueField={EntityWithIdAndDescriptionFields.Description}
              selectNotNulleable
            />
          );
        }
      });
    });

    return cols;
  }, [headers, indexMap, control, loadAforoTipos, permiteEditar]);

  return (
    <TableList
      entityList={rows}
      columns={columns}
      isLoading={isLoading}
      error={false}
    />
  );
};

export default AFOPUTablaValoresEditorGrid;